// src/components/storefront/structured-widgets/ecommerce/ProductCard.tsx
//
// Product card with multiple design variants.
// Receives a card-sized ProductData (see toCardProduct) + HexMap; QuickView
// fetches the full product by slug when opened.
// All variants share: color swatches, availability, variant price range.

"use client";

import { Heart, ShoppingBag, ShoppingCartSimple } from "@phosphor-icons/react";
import Link from "next/link";
import { useMemo, useState } from "react";
import { ProductImagePlaceholder } from "@/components/storefront/ProductImagePlaceholder";
import { CrossfadeImage } from "@/components/storefront/CrossfadeImage";
import { SafeImage } from "@/components/storefront/SafeImage";
import StockBadge from "@/components/storefront/StockBadge";
import { Button } from "@/components/storefront/ui/button";
import { IconButton } from "@/components/storefront/ui/icon-button";
import { toast } from "@/components/storefront/ui/toast";
import { useCart } from "@/lib/cart/CartContext";
import { CartStockError } from "@/lib/cart/stock";
import { useCurrency } from "@/lib/currency/CurrencyContext";
import { formatPrice } from "@/lib/currency/formatPrice";
import { effectiveVariantPrice } from "@/lib/storefront/products/variantPrice";
import { useStockDisplay } from "@/lib/storefront/stock-display";
import { useWishlistEnabled } from "@/lib/storefront/wishlist-enabled";
import type {
  CardShape,
  CardVariant,
} from "@/lib/structured-builder/widget-types/product-collection";
import { CARD_SHAPE_ASPECT } from "@/lib/structured-builder/widget-types/product-collection";
import {
  defaultProductUnit,
  unitPrice as toUnitPrice,
  unitMinBase,
} from "@/lib/units/convert";
import { useQuickView } from "./QuickViewProvider";
import type { HexMap, ProductData } from "./types";
import {
  extractVariantOptions,
  hasVariants,
  pickSpecChips,
  specChipValue,
  resolveSale,
} from "./types";

type ProductCardProps = {
  product: ProductData;
  hexMap: HexMap;
  locale: string;
  variant?: CardVariant;
  /** Image shape. Defaults to the 3:4 portrait the listing grid uses. */
  shape?: CardShape;
  categoryName?: string;
  showWishlist?: boolean;
  showAddToCart?: boolean;
  showCategoryTag?: boolean;
};

export function ProductCard({
  product,
  hexMap,
  locale,
  variant = "modern",
  shape = "rectangle",
  categoryName,
  showWishlist = true,
  showAddToCart = true,
  showCategoryTag = true,
}: ProductCardProps) {
  const isAr = locale === "ar";
  const wishlistEnabled = useWishlistEnabled();
  const { addItem } = useCart();
  const { currency } = useCurrency();
  const stockDisplay = useStockDisplay();
  const name = isAr ? product.name_ar : product.name_en;
  const href = `/${locale}/products/${product.slug ?? product.id}`;
  const firstImage = product.product_images[0]?.url ?? null;
  const firstImageBlur = product.product_images[0]?.blur_data_url ?? null;

  const [activeImage, setActiveImage] = useState(firstImage);
  const [activeColor, setActiveColor] = useState<string | null>(null);
  const { openQuickView } = useQuickView();
  // Open the global, URL-driven quick view; the slug is also the shareable
  // `?quickview=<slug>` URL. No preload: this card holds a trimmed product (no
  // descriptions, first image only), so the dialog fetches the full one — as
  // the listing-page card already does.
  const handleQuickView = () => openQuickView(product.slug ?? product.id);

  const isVariable = hasVariants(product);

  // The default selling unit, when the product has any. A product with no
  // units (the common case) keeps this null and every path below renders
  // exactly as it did before this feature existed.
  const defaultUnit = defaultProductUnit(product.units ?? []);

  /**
   * Full-width CTA on the `modern-cta` card.
   *
   * A variable product has no meaningful price or stock until a variant is
   * chosen, so it opens the quick view to pick one rather than adding
   * something arbitrary — the same split the storefront card and the PDP use.
   */
  const [cardAdding, setCardAdding] = useState(false);
  const selectOptionsLabel = isAr ? "تحديد الخيارات" : "Select options";

  async function handleCardAddToCart() {
    if (isVariable) {
      handleQuickView();
      return;
    }
    setCardAdding(true);
    try {
      const minQty = defaultUnit
        ? unitMinBase(product.min_order_qty ?? 1, defaultUnit.factor)
        : (product.min_order_qty ?? 1);
      await addItem(
        product.id,
        minQty,
        minQty,
        null,
        product.track_stock ? (product.stock ?? null) : null,
        null,
        defaultUnit?.id ?? null,
      );
      toast.success(isAr ? "تمت الإضافة للسلة" : "Added to cart");
    } catch (err) {
      if (err instanceof CartStockError) {
        toast.danger(
          isAr
            ? `الكمية المتاحة في المخزون ${err.available} فقط`
            : `Only ${err.available} in stock`,
        );
      } else {
        toast.danger(
          isAr
            ? "تعذّر إضافة المنتج. حاول مرة أخرى."
            : "Could not add product. Please try again.",
        );
      }
    } finally {
      setCardAdding(false);
    }
  }

  const { colors, otherOptions } = useMemo(
    () => extractVariantOptions(product, hexMap),
    [product, hexMap],
  );

  const addToCartLabel = isAr ? "إضافة إلى السلة" : "Add to Cart";

  const priceDisplay = useMemo(() => {
    if (!isVariable) return formatPrice(product.price, currency);
    // Unpriced variants fall back to the product price rather than dragging
    // the range down to 0.
    const prices = product.product_variants.map((v) =>
      effectiveVariantPrice(v.price, product.price),
    );
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (min === max) return formatPrice(min, currency);
    return `${formatPrice(min, currency)} - ${formatPrice(max, currency)}`;
  }, [isVariable, product, currency]);

  // Active sale campaign, resolved once and shared by every card variant.
  const sale = resolveSale(product);

  /**
   * Price line for a card variant. Without a sale it's just the regular price
   * (or variant range) in the variant's own typography; with one, the regular
   * price is struck through beside the discounted price and a −X% pill.
   */
  function renderPrice(
    primaryClass: string,
    wrapperClass = "",
    /** Set only on the amount (formatPrice's "1234.00"), never on the
     *  currency symbol or unit name, which can be Arabic. */
    numClass = "",
  ) {
    const amount = (text: string) => {
      if (!numClass) return text;
      const cut = text.indexOf(" ");
      if (cut < 0) return <span className={numClass}>{text}</span>;
      return (
        <>
          <span className={numClass}>{text.slice(0, cut)}</span>
          {text.slice(cut)}
        </>
      );
    };
    // Unit pricing headline: the per-unit price (e.g. per carton) leads, with
    // the base (per-piece) price underneath in a small/muted line — same split
    // ProductDetailClient uses. Scoped to the simple, no-sale case: a variable
    // product's range and an active sale keep their existing treatment below.
    if (defaultUnit && !isVariable && !sale) {
      const unitLabel = isAr
        ? defaultUnit.name_ar
        : (defaultUnit.name_en ?? defaultUnit.name_ar);
      return (
        <span
          className={`flex flex-col gap-0.5 min-w-0 max-w-full ${wrapperClass}`}
        >
          <span className={`min-w-0 max-w-full truncate ${primaryClass}`}>
            {amount(
              formatPrice(
                toUnitPrice(product.price, defaultUnit.factor),
                currency,
              ),
            )}
            {" / "}
            {unitLabel}
          </span>
          {product.base_unit && (
            <span className="min-w-0 max-w-full truncate text-xs text-muted-foreground">
              {amount(formatPrice(product.price, currency))}
              {" / "}
              {isAr
                ? product.base_unit.name_ar
                : (product.base_unit.name_en ?? product.base_unit.name_ar)}
            </span>
          )}
        </span>
      );
    }
    return (
      // flex-wrap, not a single line: a variable product on sale renders the
      // struck-through range, the discounted price and the −X% pill, which does
      // not fit a half-width mobile column. Without wrapping those escape the
      // card and get clipped by its rounded overflow-hidden shell.
      <span
        className={`flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 min-w-0 max-w-full ${wrapperClass}`}
      >
        {sale && (
          <span
            data-sale-original=""
            className="min-w-0 max-w-full truncate text-xs text-muted-foreground line-through"
          >
            {isVariable
              ? priceDisplay.split(" - ").map((part, i) => (
                  <span key={part}>
                    {i > 0 && " - "}
                    {amount(part)}
                  </span>
                ))
              : amount(formatPrice(sale.original, currency))}
          </span>
        )}
        <span className={`min-w-0 max-w-full ${primaryClass}`}>
          {/* A variable product renders as "min - max". Two amounts plus two
              currency symbols do not fit a half-width mobile column at the
              variant's own type size, so a range is scaled down there via `em`
              (relative, so every variant stays proportional) and returns to
              full size from `sm` up. Each amount keeps its currency symbol
              attached; only the separator may break. */}
          {(() => {
            const parts = (
              sale ? formatPrice(sale.current, currency) : priceDisplay
            ).split(" - ");
            const isRange = parts.length > 1;
            return (
              <span
                className={isRange ? "text-[0.7em] sm:text-[1em]" : undefined}
              >
                {parts.map((part, i) => (
                  <span key={part}>
                    {i > 0 && " - "}
                    <span className="whitespace-nowrap">{amount(part)}</span>
                  </span>
                ))}
              </span>
            );
          })()}
        </span>
        {sale && (
          <span
            data-sale-pill=""
            className="rounded-full bg-destructive/10 text-destructive text-[10px] font-semibold px-1.5 py-0.5 leading-none"
          >
            −{sale.percentOff}%
          </span>
        )}
      </span>
    );
  }

  // One media class for every design. The shape is a builder setting, so the
  // six variants must not each hardcode their own ratio.
  const mediaAspect = CARD_SHAPE_ASPECT[shape] ?? CARD_SHAPE_ASPECT.rectangle;

  /**
   * The swatch row used to sit in a fixed `h-5` (20px) clip box, which cut
   * 12px off every image swatch (those are 32px) and 2px off every name pill.
   * `min-h-5` keeps the row's reserved space so cards without variants still
   * line up, while letting a taller row show in full; `overflow-hidden` still
   * clips the row horizontally, which is all it was ever meant to do.
   */
  const variantRowBox = "min-h-5 overflow-hidden";

  const displayColors = colors.slice(0, 7);
  const hasMoreColors = colors.length > 7;

  // Card-level stock for the badge: simple products use their own stock;
  // variable products aggregate variant stock (null when any variant is
  // untracked ⇒ treated as unlimited). StockBadge then honors the store's
  // stock-display setting (hide / low-only / exact).
  const cardTrackStock = isVariable
    ? product.product_variants.length > 0 &&
      product.product_variants.every((v) => v.stock !== null)
    : product.track_stock;
  const cardStock = isVariable
    ? product.product_variants.some((v) => v.stock === null)
      ? null
      : product.product_variants.reduce(
          (s, v) => s + Math.max(0, v.stock ?? 0),
          0,
        )
    : product.stock;
  const stockBadge = (
    <StockBadge stock={cardStock} trackStock={cardTrackStock} />
  );

  // ── Shared blocks used across all variants ──

  const placeholderNode = <ProductImagePlaceholder alt={name} />;
  // Every image fills its tile. Framing used to branch on a `.png` extension
  // as a stand-in for "has transparency", which was only ever a guess — it
  // inset opaque PNG photos while their JPEG neighbours went edge-to-edge.
  const imageNode = activeImage ? (
    <CrossfadeImage
      src={activeImage}
      alt={name}
      fill
      sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
      className={
        // Glass cards hold transparent cut-outs: never crop or zoom a product.
        variant === "glass-spec"
          ? "object-contain p-4"
          : "object-cover transition-transform duration-300 group-hover:scale-105"
      }
      blurSrc={firstImage}
      blurDataURL={firstImageBlur}
      fallback={placeholderNode}
    />
  ) : (
    placeholderNode
  );

  function renderVariantRow() {
    if (
      !isVariable ||
      (displayColors.length === 0 && otherOptions.length === 0)
    )
      return null;
    return (
      // Single line by design: the callers wrap this in a fixed h-5 box, so a
      // wrapped second line would be clipped anyway. Swatches keep their size
      // and the availability text ellipsises into whatever room is left.
      <div className="flex flex-nowrap items-center gap-1.5 min-w-0">
        {displayColors.map((c) => {
          const isActive = activeColor === c.name;
          const onClick = () => {
            setActiveColor(c.name);
            if (c.imageUrl) setActiveImage(c.imageUrl);
          };
          // Image-thumbnail swatch (Nike-style) when the variant carries an
          // assigned image. Falls back to a hex dot, then a name pill.
          if (c.imageUrl) {
            return (
              <button
                key={c.name}
                type="button"
                title={c.name}
                aria-label={c.name}
                onClick={onClick}
                className={`relative h-8 w-8 shrink-0 overflow-hidden rounded-md cursor-pointer border-2 transition-all z-[2] ${
                  isActive
                    ? "border-primary ring-1 ring-primary/40 scale-105"
                    : "border-border"
                }`}
              >
                {/* Fixed 32×32, not `fill` + sizes="32px": a fixed width
                    emits a 1x/2x srcSet (2 URLs) where `fill` emits every
                    configured width (13) — ~2.5 KB of markup per swatch,
                    and a card grid carries dozens. No blur: it's a 32px dot. */}
                <SafeImage
                  src={c.imageUrl}
                  alt={c.name}
                  width={32}
                  height={32}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  skipBlur
                />
              </button>
            );
          }
          if (c.hex) {
            return (
              <button
                key={c.name}
                type="button"
                title={c.name}
                onClick={onClick}
                className={`h-4 w-4 shrink-0 rounded-full cursor-pointer border transition-all relative z-[2] ${
                  isActive
                    ? "border-primary ring-1 ring-primary/40 scale-110"
                    : "border-border"
                }`}
                style={{ backgroundColor: c.hex }}
              />
            );
          }
          // Name-only pill when there's no hex paired with the colour name.
          return (
            <button
              key={c.name}
              type="button"
              onClick={onClick}
              className={`h-5 shrink-0 px-1.5 rounded-full border text-[10px] font-medium leading-none cursor-pointer transition-colors relative z-[2] max-w-[80px] truncate ${
                isActive
                  ? "bg-foreground text-background border-foreground"
                  : "bg-subtle text-foreground border-border hover:border-foreground/40"
              }`}
            >
              {c.name}
            </button>
          );
        })}
        {hasMoreColors && (
          <span className="text-[10px] text-muted-foreground shrink-0">
            +{colors.length - 7}
          </span>
        )}
        {otherOptions.map((g) => (
          <span
            key={g.key}
            className="text-[10px] text-muted-foreground min-w-0 truncate"
          >
            {displayColors.length > 0 && (
              <span className="text-muted-foreground mx-1">·</span>
            )}
            {isAr ? "متوفر" : "Available"}: {g.values.slice(0, 4).join(" · ")}
            {g.values.length > 4 && ` +${g.values.length - 4}`}
          </span>
        ))}
      </div>
    );
  }

  function renderWishlist(position: string) {
    // The store-wide switch is a floor under the widget's own toggle: off
    // here means gone everywhere, regardless of what the section asked for.
    if (!showWishlist || !wishlistEnabled) return null;
    return (
      <div className={position}>
        <IconButton
          variant="soft"
          color="default"
          size="sm"
          shape="rounded"
          icon={<Heart />}
          aria-label={isAr ? "إضافة إلى المفضلة" : "Add to wishlist"}
          className="bg-card shadow-sm"
        />
      </div>
    );
  }

  // ── Variant renderers ──

  const card = (() => {
    switch (variant) {
      // ─── Modern: swatches + quick view ───
      case "modern":
        return (
          <div className="group h-full flex flex-col rounded-2xl overflow-hidden border border-[#f0eff2] bg-card shadow-[0_1px_4px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-shadow">
            <div className={`relative ${mediaAspect} bg-[#f9f9fb]`}>
              <Link
                href={href}
                className="absolute inset-0 z-[1]"
                aria-label={name}
              />
              {imageNode}
            </div>
            <div className="px-4 pt-3 pb-4 flex-1 flex flex-col">
              {showCategoryTag && categoryName && (
                <div className="mb-1.5">
                  <CategoryTag name={categoryName} />
                </div>
              )}
              <div className={`mb-1.5 ${variantRowBox}`}>
                {renderVariantRow()}
              </div>
              <Link href={href} className="block">
                <h3 data-card-name="" className="text-[15px] font-medium text-muted-foreground leading-snug line-clamp-1 group-hover:text-foreground transition-colors">
                  <bdi dir="auto">{name}</bdi>
                </h3>
              </Link>
              <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-2 mt-auto pt-2">
                {renderPrice("text-lg font-bold text-foreground")}
                {showAddToCart && (
                  <div className="relative z-[2] shrink-0">
                    <IconButton
                      variant="solid"
                      color="primary"
                      size="md"
                      shape="rounded"
                      icon={<ShoppingBag />}
                      onClick={handleQuickView}
                      aria-label={addToCartLabel}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      // ─── Glass + specs: spec chips, mono numbers, stock bar ───
      case "glass-spec": {
        const chips = pickSpecChips(product.specs);
        // Coarse buckets, not a percentage: the bar says "few / some / plenty"
        // without implying a capacity the store never stated.
        const showStock =
          stockDisplay !== "hide" && cardTrackStock && cardStock != null;
        const stockFill =
          cardStock == null || cardStock <= 3
            ? "w-1/4 glass-stock-low"
            : cardStock <= 6
              ? "w-1/2 bg-primary"
              : "w-4/5 bg-primary";
        return (
          <div className="glass-card group relative h-full flex flex-col gap-3 rounded-[20px] border p-4 motion-safe:transition-transform motion-safe:duration-200 motion-safe:hover:-translate-y-1 motion-safe:focus-within:-translate-y-1">
            <Link
              href={href}
              className="absolute inset-0 z-[1] rounded-[20px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={name}
            />
            <div className={`relative ${mediaAspect} overflow-hidden rounded-xl bg-subtle`}>
              {imageNode}
            </div>
            {chips.length > 0 && (
              <ul className="flex flex-wrap gap-1.5">
                {chips.map((c) => (
                  <li
                    key={`${c.key_en}-${specChipValue(c)}`}
                    data-testid="spec-chip"
                    dir="ltr"
                    className="rounded-md border border-border bg-subtle px-2 py-0.5 font-mono text-xs tabular-nums text-muted-foreground"
                  >
                    {specChipValue(c)}
                  </li>
                ))}
              </ul>
            )}
            <h3 className="text-[15px] font-semibold leading-snug text-foreground line-clamp-2">
              <bdi dir="auto">{name}</bdi>
            </h3>
            <div className="mt-auto flex flex-col gap-2">
              <div data-testid="glass-price">
                {renderPrice(
                  "text-lg font-bold text-foreground",
                  "",
                  "font-mono tabular-nums",
                )}
              </div>
              {showStock &&
                (cardStock > 0 ? (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs text-muted-foreground">
                      <span className="font-mono tabular-nums">{cardStock}</span>{" "}
                      {isAr ? "متوفر" : "in stock"}
                    </span>
                    <div className="h-1 overflow-hidden rounded-full bg-muted">
                      <div
                        data-testid="stock-fill"
                        className={`h-full rounded-full ${stockFill}`}
                      />
                    </div>
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {isAr ? "نفد المخزون" : "Out of stock"}
                  </span>
                ))}
            </div>
            {showAddToCart && (
              <div className="relative z-[2]">
                <Button
                  variant="soft"
                  color="primary"
                  size="sm"
                  fullWidth
                  onClick={handleCardAddToCart}
                  disabled={cardAdding || (showStock && cardStock <= 0)}
                >
                  {isVariable ? selectOptionsLabel : addToCartLabel}
                </Button>
              </div>
            )}
          </div>
        );
      }

      // ─── Rating: heart + swatches ───

      case "modern-cta":
        return (
          <div className="group h-full flex flex-col rounded-2xl overflow-hidden border border-[#f0eff2] bg-card shadow-[0_1px_4px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-shadow">
            <div className={`relative ${mediaAspect} bg-[#f9f9fb]`}>
              <Link
                href={href}
                className="absolute inset-0 z-[1]"
                aria-label={name}
              />
              {imageNode}
            </div>
            <div className="px-4 pt-3 pb-4 flex-1 flex flex-col">
              {showCategoryTag && categoryName && (
                <div className="mb-1.5">
                  <CategoryTag name={categoryName} />
                </div>
              )}
              <div className={`mb-1.5 ${variantRowBox}`}>
                {renderVariantRow()}
              </div>
              <Link href={href} className="block">
                <h3 data-card-name="" className="text-[15px] font-medium text-muted-foreground leading-snug line-clamp-1 group-hover:text-foreground transition-colors">
                  <bdi dir="auto">{name}</bdi>
                </h3>
              </Link>
              <div className="mt-auto pt-2">
                {renderPrice("text-lg font-bold text-foreground")}
              </div>
              {showAddToCart && (
                <div className="relative z-[2] pt-2">
                  <Button
                    variant="solid"
                    color="primary"
                    size="sm"
                    fullWidth
                    leftIcon={<ShoppingBag weight="bold" />}
                    onClick={handleCardAddToCart}
                    disabled={cardAdding}
                    aria-label={
                      isVariable ? selectOptionsLabel : addToCartLabel
                    }
                  >
                    {isVariable ? selectOptionsLabel : addToCartLabel}
                  </Button>
                </div>
              )}
            </div>
          </div>
        );

      // ─── Rating: heart + swatches ───
      case "rating":
        return (
          <div className="group h-full flex flex-col rounded-2xl overflow-hidden bg-card shadow-[0_1px_6px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)] transition-shadow">
            <div className={`relative ${mediaAspect} bg-[#f4f4f6]`}>
              <Link
                href={href}
                className="absolute inset-0 z-[1]"
                aria-label={name}
              />
              {imageNode}
              {renderWishlist("absolute top-3 end-3 z-[2]")}
            </div>
            <div className="px-4 pt-3 pb-4 flex-1 flex flex-col">
              <Link href={href} className="block">
                <h3 className="text-[15px] font-medium text-foreground leading-snug line-clamp-2 group-hover:text-foreground transition-colors">
                  <bdi dir="auto">{name}</bdi>
                </h3>
              </Link>
              <div className={`mt-1.5 ${variantRowBox}`}>
                {renderVariantRow()}
              </div>
              <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-2 mt-auto pt-1.5">
                {renderPrice("text-xl font-bold text-foreground")}
                {showAddToCart && (
                  <div className="relative z-[2] shrink-0">
                    <IconButton
                      variant="solid"
                      color="primary"
                      size="md"
                      shape="rounded"
                      icon={<ShoppingBag />}
                      onClick={handleQuickView}
                      aria-label={addToCartLabel}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      // ─── Minimal: clean, no buttons on image ───
      case "minimal":
        return (
          <div className="group h-full flex flex-col">
            <div
              className={`relative ${mediaAspect} overflow-hidden rounded-2xl bg-[#f5f5f7]`}
            >
              <Link
                href={href}
                className="absolute inset-0 z-[1]"
                aria-label={name}
              />
              {imageNode}
            </div>
            <div className="mt-3 px-0.5">
              <Link href={href} className="block">
                <h3 data-card-name="" className="text-sm font-medium text-foreground leading-snug line-clamp-1 group-hover:text-foreground transition-colors">
                  <bdi dir="auto">{name}</bdi>
                </h3>
              </Link>
              <div className={`mt-1.5 ${variantRowBox}`}>
                {renderVariantRow()}
              </div>
              {renderPrice("text-sm font-semibold text-foreground", "mt-1")}
            </div>
          </div>
        );

      // ─── Elegant: tall image, hover cart overlay ───
      case "elegant":
        return (
          <div className="group relative h-full flex flex-col rounded-2xl overflow-hidden border border-border bg-card shadow-[0_1px_4px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-shadow">
            <div
              className={`relative ${mediaAspect} overflow-hidden bg-[#f5f5f7]`}
            >
              <Link
                href={href}
                className="absolute inset-0 z-[1]"
                aria-label={name}
              />
              {imageNode}
              {renderWishlist("absolute top-3 end-3 z-[2]")}
              {showAddToCart && (
                <div className="absolute bottom-0 inset-x-0 z-[2] translate-y-full group-hover:translate-y-0 transition-transform duration-200">
                  <Button
                    variant="solid"
                    color="primary"
                    size="sm"
                    fullWidth
                    className="rounded-none backdrop-blur-sm"
                    leftIcon={<ShoppingCartSimple weight="bold" />}
                    onClick={handleQuickView}
                    aria-label={isAr ? "عرض سريع" : "Quick view"}
                  >
                    {isAr ? "عرض سريع" : "Quick View"}
                  </Button>
                </div>
              )}
            </div>
            <div className="px-4 pt-3 pb-4 flex-1 flex flex-col">
              <div className={`mb-1 ${variantRowBox}`}>
                {renderVariantRow()}
              </div>
              <Link href={href} className="block">
                <h3 data-card-name="" className="text-sm font-medium text-muted-foreground leading-snug line-clamp-1 group-hover:text-foreground transition-colors">
                  <bdi dir="auto">{name}</bdi>
                </h3>
              </Link>
              {renderPrice("text-[15px] font-bold text-foreground", "mt-1.5")}
            </div>
          </div>
        );

      // ─── Framed: white card, inset image ───
      case "dark":
        return (
          <div className="group h-full flex flex-col rounded-2xl bg-card shadow-[0_1px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_28px_rgba(0,0,0,0.12)] transition-shadow overflow-hidden">
            <div className="p-6 pb-0">
              <div
                className={`relative ${mediaAspect} overflow-hidden rounded-lg bg-[#f0f0f2]`}
              >
                <Link
                  href={href}
                  className="absolute inset-0 z-[1]"
                  aria-label={name}
                />
                {imageNode}
                {renderWishlist("absolute top-2 end-2 z-[2]")}
              </div>
            </div>
            <div className="px-6 pt-6 pb-6 flex-1 flex flex-col">
              <div className={`mb-2 ${variantRowBox}`}>
                {renderVariantRow()}
              </div>
              <Link href={href} className="block">
                <h3 data-card-name="" className="text-base font-semibold text-foreground leading-snug line-clamp-1 group-hover:text-muted-foreground transition-colors">
                  <bdi dir="auto">{name}</bdi>
                </h3>
              </Link>
              <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-2 mt-auto pt-2">
                {renderPrice("text-lg font-bold text-foreground")}
                {showAddToCart && (
                  <div className="relative z-[2] shrink-0">
                    <IconButton
                      variant="solid"
                      color="primary"
                      size="md"
                      shape="rounded"
                      icon={<ShoppingBag />}
                      onClick={handleQuickView}
                      aria-label={addToCartLabel}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );
    }
  })();

  // Stock badge overlays the top-start corner across every design variant
  // (StockBadge renders nothing when the store hides stock or it isn't low/exact).
  return (
    // h-full so the variant roots' own `h-full` has a definite height to
    // resolve against. The grid stretches this wrapper directly, but the
    // carousel puts its own fixed-width slot in between — without this the
    // wrapper is an auto-height block and every card sizes to its content.
    <div className="relative h-full">
      {variant !== "glass-spec" && (
        <div className="absolute top-3 start-3 z-[3] pointer-events-none">
          {stockBadge}
        </div>
      )}
      {card}
    </div>
  );
}

/**
 * Category pill on the modern cards. Tinted from the store's own primary
 * colour — it used to be hard-coded purple, which clashed with every brand.
 */
export function CategoryTag({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-primary bg-primary/10 rounded-full px-2.5 py-0.5">
      <span className="w-1.5 h-1.5 rounded-full bg-primary" />
      {name}
    </span>
  );
}
